/**
 * MainCard Component
 *
 * A flexible, responsive card layout with an optional header section and backdrop blur.
 * - Header supports `title`, `subtitle`, an inline `filter` area, and `secondary` actions.
 * - Includes sensible defaults for spacing and a strong backdrop-blur effect for glassmorphism UIs.
 * - Children are rendered as the card body.
 *
 * Props:
 *   - `title?: ReactNode` — Main heading. If a `string` is provided it will be rendered inside an `h2`.
 *   - `subtitle?: ReactNode` — Subheading displayed under the title (smaller, muted text).
 *   - `filter?: ReactNode` — Optional filter/control area (rendered to the right of the title on wide screens).
 *   - `secondary?: ReactNode` — Optional secondary actions (e.g. buttons, menus) rendered in the header.
 *   - `headerTitleClass?: string` — Additional classes applied to the title element (for typography overrides).
 *   - `headerClass?: string` — Additional classes applied to the header container (for layout overrides).
 *   - `className?: string` — Additional classes applied to the root `<main>` element.
 *   - `children: ReactNode` — Card body content.
 *
 * Behavior / accessibility:
 *   - When `title` is a `string` it uses an `h2` (semantic heading). Pass a custom element to change semantics.
 *   - Header is hidden when neither `title` nor `secondary` are provided.
 *
 * Example:
 * ```tsx
 * <MainCard
 *   title="Tasks"
 *   subtitle="Last 7 days"
 *   filter={<TaskFilter />}
 *   secondary={<Button>New</Button>}
 *   headerTitleClass="text-lg"
 *   className="max-w-4xl"
 * >
 *   <TasksTable />
 * </MainCard>
 * ```
 *
 * Notes:
 *   - The component applies `backdrop-filter: blur(20px)` inline to ensure the blur works across browsers.
 */
"use client";
import React from "react";
import { cn } from "@/lib/utils";

const MainCard = ({
  children,
  title,
  secondary,
  filter,
  subtitle,
  headerTitleClass,
  headerClass,
  className,
}: React.PropsWithChildren<{
  title?: React.ReactNode;
  secondary?: React.ReactNode;
  filter?: React.ReactNode;
  subtitle?: React.ReactNode;
  headerTitleClass?: string;
  headerClass?: string;
  className?: string;
}>) => {
  const hasHeader = Boolean(
    (typeof title === "string" && title) ||
      React.isValidElement(title) ||
      (Array.isArray(title) && title.length > 0) ||
      (typeof secondary === "string" && secondary) ||
      React.isValidElement(secondary) ||
      (Array.isArray(secondary) && secondary.length > 0),
  );

  const titleClass = cn(
    "text-left font-bold mb-1 md:mb-2 text-foreground",
    headerTitleClass,
  );

  return (
    <main
      className={cn(
        className,
        `rounded-md py-3 px-2 border transition-colors duration-200
        bg-card/30 border-border/50
        text-card-foreground
        backdrop-blur-lg backdrop-filter 
        md:py-4 md:px-4`,
      )}
      style={{ backdropFilter: "blur(20px)" }}
    >
      {hasHeader && (
        <>
          <section
            className={cn(
              headerClass,
              "flex justify-between items-start md:items-center flex-col md:flex-row gap-2",
            )}
          >
            <aside className="flex-1">
              {title &&
                (typeof title === "string" ? (
                  <h2 className={titleClass}>
                    {title}
                    {subtitle && (
                      <span className="block mt-.5 text-muted-foreground text-xs font-normal">
                        {subtitle}
                      </span>
                    )}
                  </h2>
                ) : (
                  <>
                    <div className="text-foreground">{title}</div>
                    {subtitle && (
                      <span className="block mt-.5 text-muted-foreground text-xs font-normal">
                        {subtitle}
                      </span>
                    )}
                  </>
                ))}
            </aside>
            <aside>{filter}</aside>
            <div>{secondary}</div>
          </section>
          <hr className="mt-3 mb-3 border-border/50" />
        </>
      )}
      <div className="text-foreground flex-1 min-h-0 flex flex-col">
        {children}
      </div>
    </main>
  );
};

export default MainCard;
